import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Target, Settings2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { fmtUsd } from "@/lib/format";

const BANDS_KEY = "cc:bands";
const DEFAULTS = { mcap: "50000,250000,1000000,10000000", hold: "10,60,1440", size: "50,250,1000" };
const loadBands = () => { try { return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(BANDS_KEY) || "{}")) }; } catch { return DEFAULTS; } };
const pnlCls = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-red-400" : "text-slate-500");
const fmtH = (h) => (h == null ? "—" : h < 1 ? `${Math.max(1, Math.round(h * 60))}m` : h < 24 ? `${h.toFixed(1)}h` : `${(h / 24).toFixed(1)}d`);

function Mini({ label, value, sub, color = "" }) {
  return (
    <div className="rounded-lg border border-[#222F43] bg-[#0d1420] p-3" data-testid={`edge-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono font-extrabold text-lg mt-0.5 ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function BandTable({ title, rows, unknown, testid, hint }) {
  const max = Math.max(1, ...rows.map((r) => r.trades));
  return (
    <div className="rounded-xl border border-[#222F43] bg-[#0d1420] p-4" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-xs font-mono uppercase tracking-widest text-slate-400">{title}</h3>
        {hint && <span className="text-[10px] text-slate-600">{hint}</span>}
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.band || r.day} className="grid grid-cols-[72px_1fr_52px_64px_72px] sm:grid-cols-[96px_1fr_56px_72px_84px] gap-2 items-center text-xs">
            <span className="font-mono text-slate-300">{r.band || r.day}</span>
            <div className="h-2 rounded-full bg-[#172030] overflow-hidden"><div className={`h-full ${r.pnl >= 0 ? "bg-emerald-500/70" : "bg-red-500/70"}`} style={{ width: `${(r.trades / max) * 100}%` }} /></div>
            <span className="font-mono text-slate-500 text-right">{r.trades}×</span>
            <span className={`font-mono text-right ${r.winRate == null ? "text-slate-600" : r.winRate >= 50 ? "text-emerald-400" : "text-amber-300"}`}>{r.winRate == null ? "—" : `${r.winRate}%`}</span>
            <span className={`font-mono text-right ${pnlCls(r.pnl)}`}>{r.trades ? fmtUsd(r.pnl) : "—"}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-[72px_1fr_52px_64px_72px] sm:grid-cols-[96px_1fr_56px_72px_84px] gap-2 mt-2 text-[9px] font-mono uppercase text-slate-600">
        <span /><span /><span className="text-right">trades</span><span className="text-right">win %</span><span className="text-right">P&L</span>
      </div>
      {unknown > 0 && <p className="text-[10px] text-slate-600 mt-2">{unknown} trade{unknown === 1 ? "" : "s"} without this data (manual entries) excluded.</p>}
    </div>
  );
}

function BandEditor({ bands, onChange, onClose }) {
  const [v, setV] = useState(bands);
  const field = (k, label, help) => (
    <label className="block text-xs">
      <span className="text-slate-400">{label}</span>
      <input value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`bands-${k}`}
        className="mt-1 w-full h-9 rounded-md bg-[#0d1420] border border-[#334566] px-2 font-mono text-xs text-slate-100" />
      <span className="text-[10px] text-slate-600">{help}</span>
    </label>
  );
  return (
    <div className="rounded-xl border border-[#334566] bg-[#111723] p-4 space-y-3" data-testid="bands-editor">
      {field("mcap", "Entry market-cap cut-offs (USD)", "comma-separated, ascending · default 50000,250000,1000000,10000000")}
      {field("hold", "Hold-time cut-offs (minutes)", "default 10,60,1440 → <10m · 10m–1h · 1h–1d · 1d+")}
      {field("size", "Position-size cut-offs (USD)", "default 50,250,1000")}
      <div className="flex gap-2 justify-end">
        <button onClick={() => { setV(DEFAULTS); }} className="text-xs text-slate-400 hover:text-slate-200 px-3" data-testid="bands-reset">Reset</button>
        <button onClick={onClose} className="text-xs text-slate-400 hover:text-slate-200 px-3">Cancel</button>
        <button onClick={() => onChange(v)} className="text-xs font-bold bg-emerald-500 hover:bg-emerald-400 text-black rounded-md px-3 h-8" data-testid="bands-save">Apply</button>
      </div>
    </div>
  );
}

export default function EdgePanel({ refreshKey }) {
  const [bands, setBands] = useState(loadBands);
  const [edit, setEdit] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trades/stats", { params: { mcapBands: bands.mcap, holdBands: bands.hold, sizeBands: bands.size } });
      setData(data);
    } catch { /* keep */ } finally { setLoading(false); }
  }, [bands]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const apply = (v) => { try { localStorage.setItem(BANDS_KEY, JSON.stringify(v)); } catch {} setBands(v); setEdit(false); };

  const e = data?.edge;
  if (!loading && (!e || !data?.closedTrades)) return null;
  return (
    <Card className="bg-[#111723] border-[#222F43] p-4 sm:p-6 mb-6" data-testid="edge-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-base md:text-lg font-bold flex items-center gap-2"><Target className="h-5 w-5 text-emerald-400" /> Your Edge</h2>
          <p className="text-xs text-slate-500 mt-0.5">Closed trades only · where you actually make money, by market cap, hold time and size</p>
        </div>
        <button onClick={() => setEdit((x) => !x)} className="tap text-xs text-slate-400 hover:text-emerald-400 inline-flex items-center gap-1.5 border border-[#334566] rounded-md px-2.5 h-8" data-testid="edge-bands-btn"><Settings2 className="h-3.5 w-3.5" /> Bands</button>
      </div>
      {edit && <div className="mb-4"><BandEditor bands={bands} onChange={apply} onClose={() => setEdit(false)} /></div>}
      {loading && !e ? <div className="py-8 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-4">
            <Mini label="Profit factor" value={e.profitFactor == null ? "—" : e.profitFactor.toFixed(2)} sub="gross wins ÷ gross losses · >1.5 is solid" color={e.profitFactor >= 1 ? "text-emerald-400" : "text-red-400"} />
            <Mini label="Expectancy" value={fmtUsd(e.expectancy)} sub="avg $ per trade you take" color={pnlCls(e.expectancy)} />
            <Mini label="Avg winner" value={fmtUsd(e.avgWinner)} sub={`vs avg loser ${fmtUsd(e.avgLoser)}`} color="text-emerald-400" />
            <Mini label="Win / loss size" value={e.winLossRatio == null ? "—" : `${e.winLossRatio.toFixed(2)}×`} sub="≥1 means winners outsize losers" color={e.winLossRatio >= 1 ? "text-emerald-400" : "text-amber-300"} />
            <Mini label="Hold: winners" value={fmtH(e.medianHoldHoursWinners)} sub={`losers ${fmtH(e.medianHoldHoursLosers)} (median)`} />
            <Mini label="Streak" value={e.streak.current ? `${e.streak.current}${e.streak.currentKind === "w" ? "W" : "L"}` : "—"} sub={`best ${e.streak.longestWin}W · worst ${e.streak.longestLoss}L`} color={e.streak.currentKind === "w" ? "text-emerald-400" : "text-red-400"} />
          </div>
          <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
            <BandTable title="By entry market cap" rows={e.byEntryMcap.rows} unknown={e.byEntryMcap.unknown} testid="edge-by-mcap" />
            <BandTable title="By hold time" rows={e.byHoldTime.rows} unknown={e.byHoldTime.unknown} testid="edge-by-hold" />
            <BandTable title="By position size" rows={e.bySize.rows} unknown={e.bySize.unknown} testid="edge-by-size" />
            <BandTable title="By weekday (entry, ET)" rows={e.byWeekday} unknown={0} testid="edge-by-weekday" />
          </div>
          <p className="text-[10px] text-slate-600 font-mono mt-3">Biggest win {fmtUsd(e.biggestWin)} · biggest loss {fmtUsd(e.biggestLoss)}. Market cap at entry ≈ entry price × supply.</p>
        </>
      )}
    </Card>
  );
}
